import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { QarepaBaseSubsDirective } from '@qarepa/common';
import { ChecklistsActivitiesService } from '@qaroni-app/checklists/services/checklists-activities.service';
import { ChecklistsActivities } from '@qaroni-app/checklists/types/checklists-activities';
import { DepartmentsFiles } from '@qaroni-app/departments/types/departments-files';
import { QaroniDialogConfig } from '@qaroni-core/types/dialogs/qaroni-dialog-config';
import { FileViewerDialogComponent } from '@qaroni-shared/components/dialogs/file-viewer-dialog/file-viewer-dialog.component';
import { FileViewerDialogData } from '@qaroni-shared/types/file-viewer-dialog-data';

@Component({
  selector: 'qaroni-departments-files-preview',
  templateUrl: './departments-files-preview.component.html',
  styleUrl: './departments-files-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DepartmentsFilesPreviewComponent
  extends QarepaBaseSubsDirective
  implements OnInit
{
  readonly companyID = input<number>(0);
  readonly departmentID = input<number>(0);
  readonly checkListID = input<number>(0);
  readonly activity = input<ChecklistsActivities | null>(null);
  readonly files = input<DepartmentsFiles[]>([]);
  readonly showDeleteButton = input<boolean>(true);
  readonly enableVisualization = input<boolean>(false);
  readonly sizeClass = input<string>('size-24');

  readonly filesChange = output<DepartmentsFiles[]>();

  private departmentsActivitiesService = inject(ChecklistsActivitiesService);
  private dialog = inject(MatDialog);

  public $loading = false;
  public deletedFile: DepartmentsFiles | null = null;
  private message$ = this.departmentsActivitiesService.getMessage$();

  ngOnInit() {
    this.subs.add(this.message$.subscribe(this.getMessage));
  }

  get hasFiles(): boolean {
    return this.files().length > 0;
  }

  get getStylesClass(): string[] {
    const classes = [`${this.sizeClass()}`];

    if (this.enableVisualization()) {
      classes.push(
        'hover:-translate-y-2',
        'hover:border-2',
        'hover:border-primary',
        'hover:shadow-lg'
      );
    }

    return classes;
  }

  public onDeleteFile(file: DepartmentsFiles): void {
    const activity = this.activity();
    if (!activity) {
      return;
    }

    this.$loading = true;
    this.departmentsActivitiesService.deleteFile(
      this.companyID(),
      this.checkListID(),
      activity.activityId,
      file.activityFileId
    );
    this.deletedFile = file;
  }

  public onFileVisualization(file: DepartmentsFiles): void {
    if (!this.enableVisualization()) {
      return;
    }

    const data: FileViewerDialogData = {
      fileName: file.file,
      fileUrl: file.fileUrl,
      useViewer: false,
    };

    const dialogConfig = new QaroniDialogConfig<FileViewerDialogData>();
    dialogConfig.data = data;
    dialogConfig.width = '1024px';
    dialogConfig.height = '730px';

    this.dialog.open<FileViewerDialogComponent, FileViewerDialogData, boolean>(
      FileViewerDialogComponent,
      dialogConfig
    );
  }

  public isImageFile(fileName: string): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp'];
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    return fileExtension ? imageExtensions.includes(fileExtension) : false;
  }

  private getMessage = () => {
    if (this.deletedFile) {
      const resultFiles = this.files().filter(
        file => file.activityFileId !== this.deletedFile?.activityFileId
      );
      this.deletedFile = null;
      this.filesChange.emit(resultFiles);
    }

    this.$loading = false;
  };
}
